import React from "react";
import { IconPencil, IconTrash } from "@tabler/icons";
import { Select, Text, TextInput } from "@mantine/core";
import _ from "lodash";

import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import LocalPageList from "./localPageList";
import ActionCellRender from "./actionCellRender";
import { saveFormFields } from "../helpers/formFieldSave";
import BankProofUpload from "./bankProofUpload";
import {
  DATA_BANK_WITH_CODE,
  DATA_CONSULTANT_PAYMENT_METHOD,
} from "../data/options";

const bankLabel = (value) =>
  DATA_BANK_WITH_CODE.find((bank) => bank.value === value)?.label || value || "";

const OtherBankAccountList = ({ form: mainForm, name, readOnly, disabled, ...props }) => {
  const accounts = _.get(mainForm.values, name) ?? [];
  const locked = readOnly || disabled;

  const tableSchema = {
    hidePagination: true,
    columns: [
      {
        field: "bank",
        headerName: "Bank",
        cellRender: (col, data) => bankLabel(data.bank),
      },
      { field: "accNumber", headerName: "Account Number" },
      { field: "name", headerName: "Name on Account" },
      { field: "type", headerName: "Payment Method" },
      {
        field: "files",
        headerName: "Bank Proof",
        cellRender: (col, data) => {
          const files = Array.isArray(data?.files) ? data.files : [];
          return files
            .map((file) => file?.name || file?.filename)
            .filter(Boolean)
            .join(", ");
        },
      },
      {
        field: "_actions",
        width: 80,
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            label: "Edit",
            icon: IconPencil,
            props: {},
          },
          {
            name: "delete",
            label: "Delete",
            icon: IconTrash,
            props: {},
          },
        ],
      },
    ],
  };

  const formSchema = {
    title: "Other Bank Account",
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "720px",
    },
    initialValues: {
      type: "Bank Transfer",
      bank: "",
      accNumber: "",
      name: "",
      files: [],
    },
    layout: {
      containers: [
        {
          key: "section-other-bank",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Other Bank Account",
            titleOrder: 4,
            withBorder: false,
            mt: 0,
            mb: 10,
          },
        },
      ],
      fields: [
        {
          name: "bank",
          component: Select,
          parent: "section-other-bank",
          props: {
            required: true,
            label: "Bank",
            data: DATA_BANK_WITH_CODE,
            searchable: true,
            dropdownPosition: "bottom",
          },
        },
        {
          name: "accNumber",
          component: TextInput,
          parent: "section-other-bank",
          props: {
            required: true,
            label: "Account Number",
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "section-other-bank",
          props: {
            required: true,
            label: "Name on Account",
          },
        },
        {
          name: "type",
          component: Select,
          parent: "section-other-bank",
          props: {
            required: true,
            label: "Payment Method",
            data: DATA_CONSULTANT_PAYMENT_METHOD,
            defaultValue: "Bank Transfer",
          },
        },
        {
          name: "files",
          component: BankProofUpload,
          parent: "section-other-bank",
          props: {},
        },
      ],
    },
  };

  const onFormSubmit = (values) => {
    saveFormFields(mainForm, { [name]: values });
  };

  return (
    <>
      <Text size="sm" weight={500} mt="md">
        Other Bank Accounts
      </Text>
      <Text size="xs" color="dimmed" mb="xs">
        Additional accounts besides the primary payment account.
      </Text>
      <LocalPageList
        rows={accounts}
        onFormSubmit={onFormSubmit}
        tableSchema={tableSchema}
        formSchema={formSchema}
        readOnly={locked}
        btnLabel="Add Bank Account"
        {...props}
      />
    </>
  );
};

export default OtherBankAccountList;
